// Custom domain state and actions, plus the status labels shared by the nav and the section.
import { useCallback, useEffect, useState } from 'react'
import { adminApi } from '../../services/api'
import { READ_ONLY_MESSAGE } from '../../utils/readerAccess'

export const DOMAIN_STATUS = {
  pending_dns: { label: 'Waiting for DNS', short: 'Pending', tone: 'amber' },
  verified: { label: 'DNS verified', short: 'Verified', tone: 'blue' },
  provisioning: { label: 'Setting up', short: 'Setting up', tone: 'violet' },
  active: { label: 'Live', short: 'Live', tone: 'emerald' },
  failed: { label: 'Needs attention', short: 'Failed', tone: 'red' },
}

export const STATUS_TONES = {
  amber: 'bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-900/25 dark:text-amber-300 dark:ring-amber-800/60',
  blue: 'bg-blue-50 text-blue-700 ring-blue-200 dark:bg-blue-900/25 dark:text-blue-300 dark:ring-blue-800/60',
  violet: 'bg-violet-50 text-violet-700 ring-violet-200 dark:bg-violet-900/25 dark:text-violet-300 dark:ring-violet-800/60',
  emerald: 'bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-900/25 dark:text-emerald-300 dark:ring-emerald-800/60',
  red: 'bg-red-50 text-red-700 ring-red-200 dark:bg-red-900/25 dark:text-red-300 dark:ring-red-800/60',
}

// Loads once on mount. A failed load is shown inside the section instead of
// toasting on page open.
export function useCustomDomain({ isReader, toast }) {
  const [state, setState] = useState(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(null)
  const [action, setAction] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    setLoadError(null)
    try {
      const response = await adminApi.getCustomDomain()
      setState(response.data)
    } catch (err) {
      setLoadError(err?.response?.data?.error || 'Try again in a moment.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  // Success is visible in the section itself, so only failures toast.
  const run = useCallback(async (name, operation) => {
    if (isReader) { toast.error(READ_ONLY_MESSAGE); return false }
    setAction(name)
    try {
      const response = await operation()
      if (response?.data) setState(response.data)
      else await load()
      return true
    } catch (err) {
      const body = err?.response?.data
      if (body?.domain) setState(body)
      toast.error("Couldn't update the domain", body?.error || body?.domain?.last_error || 'Try again in a moment.')
      return false
    } finally {
      setAction(null)
    }
  }, [isReader, toast, load])

  const status = state?.configured && state?.domain ? state.domain.status : null
  return { state, status, loading, loadError, action, load, run }
}
